import React from "react";
import { Button, Card, Form, Collapse } from "react-bootstrap";
import { FiTrash2, FiChevronDown } from "react-icons/fi";
import { FaGripVertical } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { Task, DeleteTarget, SUBTASK_TYPE } from "./ProjectConfigurationTypes";
import SubtaskComponent from "./SubtaskComponent";

interface TaskComponentProps {
  task: Task;
  phaseIndex: number;
  partIndex: number;
  taskIndex: number;
  taskId: string;
  phaseId: string;
  partId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: any[];
  topicsCache: Record<string, any[]>;
  contentStatus?: {
    [SUBTASK_TYPE.VIDEO]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.NOTES]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.MCQ]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.PROJECT_CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onNameChange: (name: string) => void;
  allTasks?: Task[];
  onAddSubtask: () => void;
  onDelete: (target: DeleteTarget) => void;
  onSubtaskDragEnd: (result: DropResult) => void;
  onSelectOrAssign: (subtaskIndex: number, actionType: "select" | "assign") => void;
  onSubtaskNameChange: (subtaskIndex: number, name: string) => void;
  onMandatoryChange: (subtaskIndex: number, checked: boolean) => void;
  onSubjectChange: (subtaskIndex: number, subjectId: string) => void;
  onTopicChange: (subtaskIndex: number, topicId: string) => void;
  onSubtopicChange: (subtaskIndex: number, subtopicId: string) => void;
  onSubtaskTypeChange: (subtaskIndex: number, subtaskType: string) => void;
  onEditorChange: (subtaskIndex: number, editor: string) => void;
  onLevelChange: (subtaskIndex: number, level: string) => void;
  onContentLevelChange: (subtaskIndex: number, level: string) => void;
  onContentCountChange: (subtaskIndex: number, level: string, count: string) => void;
  onAddContent: (subtaskIndex: number) => void;
  onFetchContent: (subtaskIndex: number, subtopicId: string) => Promise<void>;
  onContentSelected: (subtaskIndex: number, contentId: string, level: string, time: string) => void;
  onPreviewContent: (subtaskIndex: number, type: typeof SUBTASK_TYPE.VIDEO | typeof SUBTASK_TYPE.NOTES, content: any) => void;
  onContentCreated: (subtaskIndex: number, level1: string[], level2: string[], level3: string[]) => void;
  onSubtaskDelete: (target: DeleteTarget) => void;
  onFetchTopics: (subjectId: string) => Promise<void>;
  onDescriptionChange?: (subtaskIndex: number, description: string) => void;
  onCcChange?: (subtaskIndex: number, ccId: string) => void;
  assignedCcIds?: string[] | null;
  generateId: (prefix: string, index: number) => string;
  isSubtaskExpanded: (subtaskId: string) => boolean;
  toggleSubtaskExpand: (subtaskId: string) => void;
  isTaskRestricted?: (phaseId: string, partId: string, taskId: string) => boolean;
  isSubtaskRestricted?: (phaseId: string, partId: string, taskId: string, subtaskId: string) => boolean;
}

/**
 * TaskComponent: Renders a task card with drag-and-drop support for subtasks
 * This component displays a single task within a part and manages its subtasks.
 * It provides functionality for:
 * - Expanding/collapsing the task
 * - Editing the task name
 * - Adding new subtasks
 * - Deleting the task
 * - Drag-and-drop reordering of subtasks
 * - Rendering SubtaskComponent children for each subtask
 * - Managing available topics and subtopics based on selected subject
 */
const TaskComponent: React.FC<TaskComponentProps> = ({
  task,
  phaseIndex,
  partIndex,
  taskIndex,
  taskId,
  phaseId,
  partId,
  isExpanded,
  projectId,
  subjects,
  topicsCache,
  contentStatus,
  dragHandleProps,
  onToggleExpand,
  onNameChange,
  onAddSubtask,
  onDelete,
  onSubtaskDragEnd,
  onSelectOrAssign,
  onSubtaskNameChange,
  onMandatoryChange,
  onSubjectChange,
  onTopicChange,
  onSubtopicChange,
  onSubtaskTypeChange,
  onEditorChange,
  onLevelChange,
  onContentLevelChange,
  onContentCountChange,
  onAddContent,
  onFetchContent,
  onContentSelected,
  onPreviewContent,
  onContentCreated,
  onSubtaskDelete,
  onFetchTopics,
  generateId,
  isSubtaskExpanded,
  toggleSubtaskExpand,
  onDescriptionChange,
  onCcChange,
  assignedCcIds,
  isTaskRestricted,
  isSubtaskRestricted,
  allTasks = []
}) => {
  const taskRestricted = isTaskRestricted ? isTaskRestricted(phaseId, partId, task.id) : false;
  
  // Check for duplicate task names within the part
  const isDuplicateTaskName = task.name && task.name.trim() !== "" && 
    allTasks.some((t, idx) => idx !== taskIndex && t.name && t.name.trim().toLowerCase() === task.name.trim().toLowerCase());
  return (
    <Card className="mb-2">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <div {...dragHandleProps} style={{ cursor: taskRestricted ? 'not-allowed' : 'grab' }}>
            <FaGripVertical />
          </div>
          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
          >
            <FiChevronDown
              size={18}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2">{task.id ? task.id.charAt(0).toUpperCase() + task.id.slice(1) : task.id}</span>
          <div>
            <Form.Control
              type="text"
              placeholder="Task Name"
              value={task.name}
              onChange={(e) => onNameChange(e.target.value)}
              style={{ width: "200px" }}
            />
            {isDuplicateTaskName && (
              <div className="text-warning mt-2">                Task name has already been used
              </div>
            )}
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-info"
            size="sm"
            onClick={onAddSubtask}
            disabled={taskRestricted}
          >
            + Subtask
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => onDelete({
              type: "task",
              id: task.id,
              name: task.name || `Task ${taskIndex + 1}`,
              phaseIndex,
              partIndex,
              taskIndex
            })}
            disabled={taskRestricted}
          >
            <FiTrash2 size={16} />
          </Button>
        </div>
      </Card.Header>
      <Collapse in={isExpanded}>
        <Card.Body>
          <DragDropContext onDragEnd={onSubtaskDragEnd}>
            <Droppable droppableId={`subtasks-${taskId}`}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {task.subtasks.map((subtask, subtaskIndex) => {
                    const subtaskId = `${taskId}-${subtask.subtask_id}`;
                    const topicsFromCache = subtask.subjectId ? topicsCache[subtask.subjectId] : null;
                    const availableTopics = Array.isArray(topicsFromCache) ? topicsFromCache : [];
                    const selectedTopic = availableTopics.find(t => t.topic_id === subtask.topicId);
                    const availableSubtopics = Array.isArray(selectedTopic?.sub_topics) ? selectedTopic.sub_topics : [];
                    
                    const subtaskRestricted = isSubtaskRestricted ? isSubtaskRestricted(phaseId, partId, task.id, subtask.subtask_id) : false;
                    return (
                      <Draggable 
                        key={subtask.subtask_id} 
                        draggableId={subtask.subtask_id} 
                        index={subtaskIndex}
                        isDragDisabled={subtaskRestricted}
                      >
                        {(provided, snapshot) => {
                          // Only apply opacity if subtask is restricted AND task is NOT restricted (to avoid compounding)
                          const shouldApplyOpacity = subtaskRestricted && !taskRestricted;
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: snapshot.isDragging ? '0' : '1rem',
                                opacity: shouldApplyOpacity ? 0.6 : 1
                              }}
                            >
                            <SubtaskComponent
                              subtask={subtask}
                              phaseIndex={phaseIndex}
                              partIndex={partIndex}
                              taskIndex={taskIndex}
                              subtaskIndex={subtaskIndex}
                              subtaskId={subtaskId}
                              taskId={taskId}
                              isExpanded={isSubtaskExpanded(subtaskId)}
                              projectId={projectId}
                              subjects={subjects}
                              availableTopics={availableTopics}
                              availableSubtopics={availableSubtopics}
                              contentStatus={contentStatus}
                              dragHandleProps={provided.dragHandleProps}
                              onToggleExpand={() => toggleSubtaskExpand(subtaskId)}
                              onNameChange={(name) => onSubtaskNameChange(subtaskIndex, name)}
                              onSelectOrAssign={(actionType) => onSelectOrAssign(subtaskIndex, actionType)}
                              onMandatoryChange={(checked) => onMandatoryChange(subtaskIndex, checked)}
                              onSubjectChange={async (e) => {
                                onSubjectChange(subtaskIndex, e.target.value);
                                if (e.target.value) {
                                  await onFetchTopics(e.target.value);
                                }
                              }}
                              onTopicChange={(e) => {
                                onTopicChange(subtaskIndex, e.target.value);
                              }}
                              onSubtopicChange={(e) => onSubtopicChange(subtaskIndex, e.target.value)}
                              onSubtaskTypeChange={(e) => onSubtaskTypeChange(subtaskIndex, e.target.value)}
                              onEditorChange={(e) => onEditorChange(subtaskIndex, e.target.value)}
                              onLevelChange={(e) => onLevelChange(subtaskIndex, e.target.value)}
                              onContentLevelChange={(level) => onContentLevelChange(subtaskIndex, level)}
                              onContentCountChange={(level, count) => onContentCountChange(subtaskIndex, level, count)}
                              onAddContent={() => onAddContent(subtaskIndex)}
                              onFetchContent={(subtopicId) => onFetchContent(subtaskIndex, subtopicId)}
                              onContentSelected={(contentId, level, time) => onContentSelected(subtaskIndex, contentId, level, time)}
                              onPreviewContent={(type, content) => onPreviewContent(subtaskIndex, type, content)}
                              onContentCreated={(level1, level2, level3) => onContentCreated(subtaskIndex, level1, level2, level3)}
                              onDelete={onSubtaskDelete}
                              onDescriptionChange={onDescriptionChange ? (description) => onDescriptionChange(subtaskIndex, description) : undefined}
                              onCcChange={onCcChange ? (ccId) => onCcChange(subtaskIndex, ccId) : undefined}
                              assignedCcIds={assignedCcIds}
                              isSubtaskRestricted={subtaskRestricted}
                              allSubtasks={task.subtasks}
                              />
                          </div>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default TaskComponent;

